// The orchestrator. Holds one ProviderState per enabled provider across votes,
// markets, and finance; a single refreshAll() fans them all out under
// Promise.allSettled so ONE failing provider never rejects the batch or blanks a
// sibling panel. Reconciliation and trend snapshots derive from the vote states.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RaceConfig } from '../types/config.ts';
import type { NormalizedResult } from '../types/results.ts';
import type { MarketResult } from '../types/markets.ts';
import type { FinanceResult } from '../types/finance.ts';
import type { ProviderState } from '../types/ui.ts';
import type { Snapshot, MarginPoint } from '../types/snapshots.ts';
import { ProviderError } from '../types/providers.ts';
import { voteProviders, marketProviders, financeProviders } from '../providers/index.ts';
import { reconcile, type Reconciliation } from '../lib/reconcile.ts';
import { makeSnapshot } from '../lib/snapshots.ts';
import { isStale, nowIso } from '../lib/time.ts';
import { useSnapshots } from './useSnapshots.ts';
import { useRefreshLoop } from './useRefreshLoop.ts';

type VoteStates = Record<string, ProviderState<NormalizedResult>>;
type MarketStates = Record<string, ProviderState<MarketResult>>;

function idle<T>(): ProviderState<T> {
  return {
    status: 'idle',
    data: null,
    error: null,
    errorKind: null,
    fetchedAt: null,
    lastAttemptAt: null,
    isStale: false,
  };
}

function errorKindOf(e: unknown): ProviderState<unknown>['errorKind'] {
  return e instanceof ProviderError ? e.kind : 'unreachable';
}

export interface LiveData {
  votes: VoteStates;
  markets: MarketStates;
  finance: ProviderState<FinanceResult>;
  reconciliation: Reconciliation;
  marginSeries: MarginPoint[];
  snapshots: Snapshot[];
  lastUpdatedAt: string | null;
  isRefreshing: boolean;
  nowMs: number;
  refreshNow: () => void;
}

export function useLiveData(race: RaceConfig): LiveData {
  const voteCfgs = useMemo(() => race.voteProviders.filter((p) => p.enabled), [race]);
  const marketCfgs = useMemo(() => race.marketProviders.filter((p) => p.enabled), [race]);
  const financeCfg = useMemo(
    () => race.financeProviders.find((p) => p.enabled) ?? null,
    [race],
  );

  const [votes, setVotes] = useState<VoteStates>(() =>
    Object.fromEntries(voteCfgs.map((p) => [p.id, idle<NormalizedResult>()])),
  );
  const [markets, setMarkets] = useState<MarketStates>(() =>
    Object.fromEntries(marketCfgs.map((p) => [p.id, idle<MarketResult>()])),
  );
  const [finance, setFinance] = useState<ProviderState<FinanceResult>>(() => idle<FinanceResult>());
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const { snapshots, marginSeries, append } = useSnapshots(race.raceId, race.snapshotCap);

  const inflight = useRef<AbortController[]>([]);
  const financeCtrl = useRef<AbortController | null>(null);
  const financeLoaded = useRef(false);
  const tickCount = useRef(0);

  const refreshAll = useCallback(async () => {
    inflight.current.forEach((c) => c.abort());
    inflight.current = [];
    setIsRefreshing(true);
    const tick = ++tickCount.current;

    const newCtrl = () => {
      const c = new AbortController();
      inflight.current.push(c);
      return c;
    };

    const runVote = async (cfgId: string) => {
      const cfg = voteCfgs.find((p) => p.id === cfgId);
      const adapter = voteProviders[cfgId];
      if (!cfg || !adapter) return;
      const ctrl = newCtrl();
      const attemptAt = nowIso();
      setVotes((m) => ({ ...m, [cfgId]: { ...(m[cfgId] ?? idle()), status: 'loading', lastAttemptAt: attemptAt } }));
      try {
        const data = await adapter.getResults(race, cfg, ctrl.signal);
        setVotes((m) => ({
          ...m,
          [cfgId]: {
            status: 'success',
            data,
            error: null,
            errorKind: null,
            fetchedAt: data.fetchedAt ?? attemptAt,
            lastAttemptAt: attemptAt,
            isStale: false,
          },
        }));
      } catch (e) {
        if (ctrl.signal.aborted) return;
        setVotes((m) => ({
          ...m,
          [cfgId]: { ...(m[cfgId] ?? idle()), status: 'error', error: (e as Error).message, errorKind: errorKindOf(e), lastAttemptAt: attemptAt },
        }));
      }
    };

    const runMarket = async (cfgId: string) => {
      const cfg = marketCfgs.find((p) => p.id === cfgId);
      const adapter = marketProviders[cfgId];
      if (!cfg || !adapter) return;
      const ctrl = newCtrl();
      const attemptAt = nowIso();
      setMarkets((m) => ({ ...m, [cfgId]: { ...(m[cfgId] ?? idle()), status: 'loading', lastAttemptAt: attemptAt } }));
      try {
        const data = await adapter.getMarket(race, cfg, ctrl.signal);
        setMarkets((m) => ({
          ...m,
          [cfgId]: { status: 'success', data, error: null, errorKind: null, fetchedAt: data.fetchedAt ?? attemptAt, lastAttemptAt: attemptAt, isStale: false },
        }));
      } catch (e) {
        if (ctrl.signal.aborted) return;
        setMarkets((m) => ({
          ...m,
          [cfgId]: { ...(m[cfgId] ?? idle()), status: 'error', error: (e as Error).message, errorKind: errorKindOf(e), lastAttemptAt: attemptAt },
        }));
      }
    };

    const runFinance = async () => {
      if (!financeCfg) return;
      const adapter = financeProviders[financeCfg.id];
      if (!adapter) return;
      // Finance has its OWN controller (not aborted by unrelated vote/market
      // refreshes) so a slow FEC fetch is never stranded mid-flight.
      financeCtrl.current?.abort();
      const ctrl = new AbortController();
      financeCtrl.current = ctrl;
      const attemptAt = nowIso();
      setFinance((s) => ({ ...s, status: 'loading', lastAttemptAt: attemptAt }));
      try {
        const data = await adapter.getFinance(race, financeCfg, ctrl.signal);
        financeLoaded.current = true;
        setFinance({ status: 'success', data, error: null, errorKind: null, fetchedAt: data.fetchedAt ?? attemptAt, lastAttemptAt: attemptAt, isStale: false });
      } catch (e) {
        if (ctrl.signal.aborted) return;
        financeLoaded.current = true; // an honest error still counts as "attempted"
        setFinance((s) => ({ ...s, status: 'error', error: (e as Error).message, errorKind: errorKindOf(e), lastAttemptAt: attemptAt }));
      }
    };

    // Fetch finance until it has loaded once, then only on its slow cadence.
    const financeThisTick =
      financeCfg != null &&
      (!financeLoaded.current || (tick - 1) % Math.max(1, race.financeRefreshEveryNTicks) === 0);

    await Promise.allSettled([
      ...voteCfgs.map((p) => runVote(p.id)),
      ...marketCfgs.map((p) => runMarket(p.id)),
      ...(financeThisTick ? [runFinance()] : []),
    ]);

    setIsRefreshing(false);
    setLastUpdatedAt(nowIso());
  }, [race, voteCfgs, marketCfgs, financeCfg]);

  // Initial fetch (and re-fetch if the race config changes).
  useEffect(() => {
    void refreshAll();
    return () => {
      inflight.current.forEach((c) => c.abort());
      inflight.current = [];
      financeCtrl.current?.abort();
    };
  }, [refreshAll]);

  // Auto-refresh loop.
  useRefreshLoop({ intervalMs: race.refreshIntervalMs, onTick: () => void refreshAll() });

  // Staleness clock — re-evaluates the isStale flags as time passes.
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, []);

  // Reconcile the successful vote sources.
  const reconciliation = useMemo<Reconciliation>(() => {
    const successful = Object.values(votes)
      .filter((s): s is ProviderState<NormalizedResult> & { data: NormalizedResult } => s.status === 'success' && s.data != null)
      .map((s) => s.data);
    return reconcile(successful, race);
  }, [votes, race]);

  // Snapshot the primary after each successful reconcile (deduped downstream).
  useEffect(() => {
    if (reconciliation.primary && reconciliation.primaryMargin) {
      append(makeSnapshot(race.raceId, reconciliation.primary, reconciliation.primaryMargin));
    }
  }, [reconciliation, race.raceId, append]);

  // Apply the live staleness flag to what the UI reads.
  const withStale = useCallback(
    <T,>(s: ProviderState<T>): ProviderState<T> => ({
      ...s,
      isStale: isStale(s.fetchedAt, race.stalenessThresholdMs, nowMs),
    }),
    [race.stalenessThresholdMs, nowMs],
  );

  const votesOut = useMemo(() => {
    const out: VoteStates = {};
    for (const [k, v] of Object.entries(votes)) out[k] = withStale(v);
    return out;
  }, [votes, withStale]);

  const marketsOut = useMemo(() => {
    const out: MarketStates = {};
    for (const [k, v] of Object.entries(markets)) out[k] = withStale(v);
    return out;
  }, [markets, withStale]);

  const financeOut = useMemo(() => withStale(finance), [finance, withStale]);

  return {
    votes: votesOut,
    markets: marketsOut,
    finance: financeOut,
    reconciliation,
    marginSeries,
    snapshots,
    lastUpdatedAt,
    isRefreshing,
    nowMs,
    refreshNow: () => void refreshAll(),
  };
}
